import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SignIn from './pages/auth/SignIn';
import Dashboard from './pages/dashboard/Dashboard';
import Courses from './pages/courses/Courses';
import MainLayout from './layouts/MainLayout';
import PopupAdmin from './pages/popup/PopupAdmin';
import Employee from './pages/employee/Employee';
import Career from './pages/career/Career';
import CareerApplicants from './pages/career/CareerApplicants';
import Slider from './pages/slider/Slider';
import Updates from './pages/updates/Updates';
import OfficeAnnouncements from './pages/announcements/OfficeAnnouncements';
import { logout, setUser } from './store/slices/authSlice';
import axiosInstance from './config/axiosConfig';
import { SECRET_KEY } from './helper';
import CryptoJS from "crypto-js";
import Swal from 'sweetalert2';
import Office from './pages/office/Office';
import RegistrarTeam from './pages/office/RegistrarTeam';
import ItServicesAdmin from './pages/office/ItServicesAdmin';
import Faculty from './pages/faculty/Faculty';
import Department from './pages/department/Department';
import CampusFacilities from './pages/facilities/CampusFacilities';
import Resource from './pages/resource/Resource';
import Research from './pages/research/Research';
import PublicationSubmissions from './pages/research/PublicationSubmissions';
import AdmissionAd from './pages/admissionAd/AdmissionAd';
import Tender from './pages/tender/Tender';
import GenericAd from './pages/genericAd/GenericAd';
import QEC from './pages/qec/QEC';
import Users from './pages/users/Users';
import ContactMessages from './pages/contact/ContactMessages';
import ContactPhones from './pages/contact/ContactPhones';
import TopBar from './pages/topBar/TopBar';
import HomePageAdmin from './pages/homePage/HomePageAdmin';
import UniversityLifeAdmin from './pages/universityLife/UniversityLifeAdmin';
import SdcBatches from './pages/sdc/SdcBatches';
import SdcPrograms from './pages/sdc/SdcPrograms';
import SdcCourses from './pages/sdc/SdcCourses';
import { canAccessModule, getDefaultRoute, isSuperAdminUser } from './helper/permissions';

const ProtectedModule = ({ moduleKey, children }) => {
  const user = useSelector((state) => state.auth.user);
  if (!canAccessModule(user, moduleKey)) {
    return <Navigate to={getDefaultRoute(user)} replace />;
  }
  return children;
};

const SuperAdminOnly = ({ children }) => {
  const user = useSelector((state) => state.auth.user);
  // Allow while profile is still loading so the Users page can open after login
  if (user && !isSuperAdminUser(user)) {
    return <Navigate to={getDefaultRoute(user)} replace />;
  }
  return children;
};

const App = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    const encryptedToken = localStorage.getItem('authToken');

    if (encryptedToken) {
      const bytes = CryptoJS.AES.decrypt(encryptedToken, SECRET_KEY);
      const authToken = bytes.toString(CryptoJS.enc.Utf8);

      validateToken(authToken).catch(() => {
        Swal.fire({
          icon: 'error',
          title: 'Session Expired!',
          text: 'Login Again',
          timer: 1500,
        });
        dispatch(logout());
      });
    } else {
      dispatch(logout());
    }
  }, []);

  const validateToken = async () => {
    const response = await axiosInstance.post('users/validate-token');
    if (response?.data?.user) {
      dispatch(setUser(response.data.user));
    }
  };

  const defaultRoute = getDefaultRoute(user);

  return (
    <Router>
      <Routes>
        <Route
          path="/signin"
          element={isAuthenticated ? <Navigate to={defaultRoute} /> : <SignIn />}
        />
        <Route
          path="/"
          element={isAuthenticated ? <MainLayout /> : <Navigate to="/signin" />}
        >
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="/programs" element={<ProtectedModule moduleKey="programs"><Courses /></ProtectedModule>} />
          <Route path="/employee" element={<ProtectedModule moduleKey="employee"><Employee /></ProtectedModule>} />
          <Route path="/faculties" element={<ProtectedModule moduleKey="faculties"><Faculty /></ProtectedModule>} />
          <Route path="/departments" element={<ProtectedModule moduleKey="departments"><Department /></ProtectedModule>} />
          <Route path="/campus-facilities" element={<ProtectedModule moduleKey="campus-facilities"><CampusFacilities /></ProtectedModule>} />
          <Route path="/facilities" element={<Navigate to="/campus-facilities" replace />} />
          <Route path="/career" element={<ProtectedModule moduleKey="career"><Career /></ProtectedModule>} />
          <Route path="/career/applicants" element={<ProtectedModule moduleKey="career-applicants"><CareerApplicants /></ProtectedModule>} />
          <Route path="/slider" element={<ProtectedModule moduleKey="slider"><Slider /></ProtectedModule>} />
          <Route path="/home-page" element={<ProtectedModule moduleKey="home-page"><HomePageAdmin /></ProtectedModule>} />
          <Route path="/university-life" element={<ProtectedModule moduleKey="university-life"><UniversityLifeAdmin /></ProtectedModule>} />
          <Route path="/updates" element={<ProtectedModule moduleKey="updates"><Updates /></ProtectedModule>} />
          <Route path="/announcements/:officeSlug" element={<ProtectedModule moduleKey="announcements"><OfficeAnnouncements /></ProtectedModule>} />
          <Route path="/offices" element={<ProtectedModule moduleKey="offices"><Office /></ProtectedModule>} />
          <Route path="/registrar-team" element={<ProtectedModule moduleKey="registrar-team"><RegistrarTeam /></ProtectedModule>} />
          <Route path="/it-services" element={<ProtectedModule moduleKey="it-services"><ItServicesAdmin /></ProtectedModule>} />
          <Route path="/publication-submissions" element={<ProtectedModule moduleKey="publication-submissions"><PublicationSubmissions /></ProtectedModule>} />
          <Route path="/resources" element={<ProtectedModule moduleKey="resources"><Resource /></ProtectedModule>} />
          <Route path="/researches" element={<ProtectedModule moduleKey="researches"><Research /></ProtectedModule>} />
          <Route path="/admissionAds" element={<ProtectedModule moduleKey="admissionAds"><AdmissionAd /></ProtectedModule>} />
          <Route path="/tender" element={<ProtectedModule moduleKey="tenders"><Tender /></ProtectedModule>} />
          <Route path="/generic-ad" element={<ProtectedModule moduleKey="generic-ads"><GenericAd /></ProtectedModule>} />
          <Route path="/qec" element={<ProtectedModule moduleKey="qec"><QEC /></ProtectedModule>} />
          <Route path="/popup-modal" element={<ProtectedModule moduleKey="popup-modal"><PopupAdmin /></ProtectedModule>} />
          <Route path="/contact-messages" element={<ProtectedModule moduleKey="contact"><ContactMessages /></ProtectedModule>} />
          <Route path="/contact-phones" element={<ProtectedModule moduleKey="contact-phones"><ContactPhones /></ProtectedModule>} />
          <Route path="/top-bar" element={<ProtectedModule moduleKey="top-bar"><TopBar /></ProtectedModule>} />
          <Route path="/sdc/batches" element={<ProtectedModule moduleKey="sdc-batches"><SdcBatches /></ProtectedModule>} />
          <Route path="/sdc/programs" element={<ProtectedModule moduleKey="sdc-programs"><SdcPrograms /></ProtectedModule>} />
          <Route path="/sdc/courses" element={<ProtectedModule moduleKey="sdc-courses"><SdcCourses /></ProtectedModule>} />
          <Route path="/users" element={<SuperAdminOnly><Users /></SuperAdminOnly>} />
        </Route>
        <Route path="*" element={<Navigate to={isAuthenticated ? defaultRoute : '/signin'} />} />
      </Routes>
    </Router>
  );
};

export default App;
